import {Box, Button, Grid2 as Grid, Stack, Typography} from '@mui/material'
import {Breadcrumb, FixedFab, LoadingPage, PageLayout} from '@goschool/components'
import {Trans, useTranslation} from 'react-i18next'
import AddIcon from '@mui/icons-material/Add'
import {useMemo, useState} from 'react'
import {useUserContext} from '@goschool/auth'
import {CreateCourseDialog} from '../components/CreateCourseDialog'

import {GoSchool} from '@goschool/routing'
import type {DocumentSnapshot, QueryDocumentSnapshot} from 'firebase/firestore'
import {isCourseArchived, type Course, type Organization} from '@goschool/model'
import {useCanCreateCourse, useOrganizationCourses} from '@goschool/model'
import {CourseCard} from '../components/CourseCard'
import {CourseContextProvider} from '@goschool/dao'
import {OrganizationSelector, useSelectedOrganization} from '../components/OrganizationSelector'
import {Link as RouterLink} from 'react-router-dom'

export function AdminBreadcrumb() {
  return <Breadcrumb to={GoSchool.admin}>
    <Trans i18nKey="course:catalog.title"/>
  </Breadcrumb>
}


export function AdminPage() {
  const {roles, user, goSchoolUser, state} = useUserContext()
  const [creating, setCreating] = useState(false)

  const organization = useSelectedOrganization()
  const canCreateCourse = useCanCreateCourse(organization?.ref, user?.uid, roles)

  if (user === undefined || state === 'pending' || goSchoolUser === undefined) {
    return <LoadingPage/>
  }


  return <PageLayout fullScreen={false} breadcrumbs={[]}>
    <Typography variant="h4" component="h1" gutterBottom={false}><Trans
      i18nKey="admin:courses.title"/></Typography>
    <Stack direction="column" gap={4} alignItems="stretch">
      <Stack direction="row" gap={2} justifyContent="stretch">
        <OrganizationSelector/>
        <Stack direction="row" gap={2} justifyContent="flex-end" flexGrow={1}>
          <Button component={RouterLink} to={'invitations'} variant="outlined">Meghívók</Button>
          <Button component={RouterLink} to={'chats'} variant="outlined">Chatek</Button>
        </Stack>
      </Stack>
      {<OrganizationAdmin snapshot={organization}/>}
    </Stack>
    {canCreateCourse &&
        <FixedFab onClick={() => setCreating(true)} color="primary" horizontal="right" vertical="bottom">
          <AddIcon/>
        </FixedFab>
    }
    <CreateCourseDialog organizationRef={organization?.ref} displayed={creating} hide={() => setCreating(false)}/>
  </PageLayout>
}

function OrganizationAdmin({snapshot}: { snapshot: DocumentSnapshot<Organization> | undefined | null }) {
  const courses = useOrganizationCourses(snapshot)
  const {t} = useTranslation()
  const organization = useMemo(
    () => snapshot?.data(),
    [snapshot]
  )
  const activeCourses = useMemo(
    () => (courses ?? []).filter(course => !isCourseArchived(course.data())),
    [courses]
  )
  const archivedCourses = useMemo(
    () => (courses ?? []).filter(course => isCourseArchived(course.data())),
    [courses]
  )


  if (organization == null) {
    return null
  }

  return <Box>
    <CourseSection title={t('course:catalog.active-courses')} courses={activeCourses}/>
    {archivedCourses.length > 0 &&
      <CourseSection title={t('course:catalog.archived-courses')} courses={archivedCourses} sx={{mt: 5}}/>}
  </Box>
}

function CourseSection({
  title,
  courses,
  sx
}: {
  title: string
  courses: QueryDocumentSnapshot<Course>[]
  sx?: Parameters<typeof Box>[0]['sx']
}) {
  if (courses.length === 0) {
    return null
  }

  return <Box sx={sx}>
    <Typography variant="h6" component="h2" sx={{mb: 2, fontWeight: 700}}>
      {title}
    </Typography>
    <Grid container spacing={4} rowSpacing={4}>
      {courses.map(course =>
        <CourseContextProvider key={course.ref.path} courseSnapshot={course}>
          <Grid size={{xs: 12, md: 4, sm: 6}}>
            <CourseCard/>
          </Grid>
        </CourseContextProvider>
      )}
    </Grid>
  </Box>
}
